import React, { useState, useEffect } from "react";
import Icon from "./Icon";
import FileIcon from "./FileIcon";
import * as api from "../../../services/api";

function FilePreviewModal({ file, onClose, onDownload, showToast }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewType, setPreviewType] = useState("none");
  const [error, setError] = useState("");

  useEffect(() => {
    if (file && file.type !== "folder" && file.id) {
      loadPreview();
    }
    return () => { setPreviewUrl(""); setError(""); };
  }, [file]);

  const loadPreview = async () => {
    setLoading(true);
    setError("");
    try {
      const url = await api.getDownloadUrl(file.id);
      if (!url) throw new Error("No URL returned");
      setPreviewUrl(url);

      const ext = (file.ext || file.name?.split(".").pop() || "").toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico"].includes(ext)) {
        setPreviewType("image");
      } else if (ext === "pdf") {
        setPreviewType("pdf");
      } else if (["mp4", "webm", "mov", "avi", "mkv"].includes(ext)) {
        setPreviewType("video");
      } else if (["mp3", "wav", "ogg", "m4a", "flac"].includes(ext)) {
        setPreviewType("audio");
      } else if (["txt", "md", "csv", "json", "log", "xml", "html", "css", "js", "py", "java", "ts"].includes(ext)) {
        setPreviewType("text");
      } else if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) {
        setPreviewType("office");
      } else {
        setPreviewType("none");
      }
    } catch (err) {
      setError(err.message);
      setPreviewUrl("");
      setPreviewType("none");
    } finally {
      setLoading(false);
    }
  };

  if (!file) return null;

  const handleDownload = () => {
    if (onDownload) onDownload(file);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return dateStr; }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="fullscreen-preview-loading">
          <Icon name="spinner" spin style={{ fontSize: 32, color: "#4285f4" }} />
          <p>Loading preview...</p>
        </div>
      );
    }

    if (error || (!previewUrl && previewType === "none")) {
      return (
        <div className="fullscreen-preview-fallback">
          <FileIcon type="file" ext={file.ext} large />
          <h4>{file.name}</h4>
          {error && error.includes("not found") ? (
            <p>File not found in storage. It may have been deleted.</p>
          ) : (
            <p>Preview not available for .{file.ext || "this"} files</p>
          )}
          <button className="preview-download-action" onClick={handleDownload}>
            <Icon name="download" /> Download to View
          </button>
        </div>
      );
    }

    switch (previewType) {
      case "image":
        return <img src={previewUrl} alt={file.name} className="fullscreen-preview-image" />;
      
      case "pdf":
        return (
          <div className="fullscreen-pdf-container">
            <object
              data={previewUrl}
              type="application/pdf"
              className="fullscreen-preview-pdf"
            >
              <embed src={previewUrl} type="application/pdf" className="fullscreen-preview-pdf" />
              <div className="fullscreen-preview-fallback">
                <Icon name="filePdf" style={{ fontSize: 48, color: "#ea4335" }} />
                <h4>{file.name}</h4>
                <p>Your browser cannot display this PDF inline.</p>
                <button className="preview-download-action" onClick={handleDownload}>
                  <Icon name="download" /> Download PDF
                </button>
                <a 
                  href={previewUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="preview-open-action"
                >
                  <Icon name="externalLink" /> Open in New Tab
                </a>
              </div>
            </object>
          </div>
        );

      case "office":
        // Use Google Docs Viewer for Office documents (DOCX, XLSX, PPTX)
        // Google fetches the file server-side, so presigned S3 URLs work
        const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(previewUrl)}&embedded=true`;
        return (
          <div className="fullscreen-office-container">
            <iframe
              src={googleViewerUrl}
              className="fullscreen-preview-office"
              title="Document Preview"
              frameBorder="0"
            />
            <div className="office-preview-hint">
              <span>Powered by Google Docs Viewer</span>
              <button className="preview-download-action small" onClick={handleDownload}>
                <Icon name="download" /> Download
              </button>
            </div>
          </div>
        );

      case "video":
        return <video src={previewUrl} controls autoPlay className="fullscreen-preview-video" />;
      
      case "audio":
        return (
          <div className="fullscreen-preview-fallback">
            <Icon name="music" style={{ fontSize: 48, color: "#4285f4", marginBottom: 16 }} />
            <h4>{file.name}</h4>
            <audio src={previewUrl} controls style={{ width: "100%", maxWidth: 400, marginTop: 16 }} />
          </div>
        );
      
      case "text":
        return <iframe src={previewUrl} className="fullscreen-preview-iframe text-preview" title="Text Preview" />;
      
      default:
        return (
          <div className="fullscreen-preview-fallback">
            <FileIcon type="file" ext={file.ext} large />
            <h4>{file.name}</h4>
            <p>Cannot preview this file type</p>
            <button className="preview-download-action" onClick={handleDownload}>
              <Icon name="download" /> Download
            </button>
          </div>
        );
    }
  };

  return (
    <div className="fullscreen-preview-overlay" onClick={onClose}>
      <div className="fullscreen-preview-container" onClick={(e) => e.stopPropagation()}>
        {/* Top Bar */}
        <div className="fullscreen-preview-topbar">
          <div className="fullscreen-preview-title">
            <FileIcon type="file" ext={file.ext} />
            <span className="fullscreen-preview-filename">{file.name}</span>
            <span className="fullscreen-preview-meta">
              {file.size && <span>{file.size}</span>}
              {file.owner && <span>• {file.owner}</span>}
              {file.modified && <span>• {formatDate(file.modified)}</span>}
            </span>
          </div>
          <div className="fullscreen-preview-actions">
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="fullscreen-btn" title="Open in new tab">
                <Icon name="externalLink" />
              </a>
            )}
            <button className="fullscreen-btn" onClick={handleDownload} title="Download">
              <Icon name="download" />
            </button>
            <button className="fullscreen-btn close-btn" onClick={onClose} title="Close (Esc)">
              <Icon name="x" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="fullscreen-preview-body">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
}

export default FilePreviewModal;
